import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

const EmailConsentPage = () => {
  const { user, getAccessTokenSilently, isLoading } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State management
  const [emailConsent, setEmailConsent] = useState(false);
  const [emailPreferences, setEmailPreferences] = useState({
    trending: true,
    newReleases: true,
    personalized: true,
    newsletter: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isEUUser, setIsEUUser] = useState(false);

  // Get user info from URL params (passed by Auth0 Post-Registration Action)
  const userEmail = searchParams.get('email') || user?.email;
  const userId = searchParams.get('user_id') || user?.sub;
  const returnUrl = searchParams.get('return_url') || '/';

  useEffect(() => {
    // Detect EU users for GDPR compliance
    detectEUUser();
  }, []);

  const detectEUUser = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const euTimezones = [
      'Europe/London',
      'Europe/Berlin',
      'Europe/Paris',
      'Europe/Rome',
      'Europe/Madrid',
      'Europe/Amsterdam',
      'Europe/Brussels',
      'Europe/Vienna',
      'Europe/Stockholm',
      'Europe/Copenhagen',
      'Europe/Helsinki',
      'Europe/Dublin',
      'Europe/Lisbon',
      'Europe/Athens',
      'Europe/Prague',
      'Europe/Warsaw',
      'Europe/Budapest',
      'Europe/Bucharest',
      'Europe/Sofia',
      'Europe/Zagreb',
    ];
    setIsEUUser(euTimezones.includes(timezone));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Prepare consent data
      const consentData = {
        user_id: userId,
        email: userEmail,
        email_marketing_consent: emailConsent,
        email_preferences: emailConsent ? emailPreferences : {},
        consent_timestamp: new Date().toISOString(),
        gdpr_applicable: isEUUser,
        ip_address: await getUserIP(),
        user_agent: navigator.userAgent,
      };

      // Send to Sendy for email subscription (if consented)
      await sendConsentToSendy(consentData);

      // Update Auth0 user metadata (always - to mark terms accepted)
      await updateAuth0UserMetadata(consentData);

      // Redirect to the app
      window.location.href = returnUrl;
    } catch (err) {
      console.error('Error submitting consent:', err);
      setError('Failed to save your preferences. Please try again.');
      setIsSubmitting(false);
    }
  };

  const sendConsentToSendy = async consentData => {
    // Only send to Sendy if user consented to email marketing
    if (!consentData.email_marketing_consent) {
      return; // Skip Sendy if no consent
    }

    // Prepare Sendy subscription data
    const sendyData = new URLSearchParams({
      api_key: 'ETem2q9ljwiJOL4EMgbP',
      email: consentData.email,
      list: 'X89243N6HRnmfnTEDhrGQ892jQ',
      name: searchParams.get('name') || 'BKFC User',
      boolean: 'true', // GDPR compliance
      // Custom fields for email preferences
      trending: consentData.email_preferences.trending ? 'Yes' : 'No',
      new_releases: consentData.email_preferences.newReleases ? 'Yes' : 'No',
      personalized: consentData.email_preferences.personalized ? 'Yes' : 'No',
      newsletter: consentData.email_preferences.newsletter ? 'Yes' : 'No',
      consent_timestamp: consentData.consent_timestamp,
      gdpr_applicable: consentData.gdpr_applicable ? 'Yes' : 'No',
    });

    const response = await fetch('https://bkfc.net/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: sendyData,
    });

    const responseText = await response.text();

    // Sendy returns different responses for success/error
    if (responseText !== '1' && !responseText.includes('Already subscribed')) {
      throw new Error(`Sendy subscription failed: ${responseText}`);
    }
  };

  const updateAuth0UserMetadata = async consentData => {
    try {
      const token = await getAccessTokenSilently();

      // Update user metadata via your backend API
      const response = await fetch('/api/user/update-email-preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          accepted_terms: true, // Mark terms as accepted (replaces the old terms form)
          email_marketing_consent: consentData.email_marketing_consent,
          email_preferences: consentData.email_preferences,
          consent_timestamp: consentData.consent_timestamp,
          gdpr_applicable: consentData.gdpr_applicable,
        }),
      });

      if (!response.ok) {
        console.warn('Failed to update user preferences via API, continuing anyway');
      }
    } catch (err) {
      console.error('Error updating Auth0 metadata:', err);
      // Don't throw here - Sendy integration is the primary method
    }
  };

  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (err) {
      return 'unknown';
    }
  };

  const handleSkip = async () => {
    // User chooses to skip email marketing but still needs terms marked as accepted
    try {
      const consentData = {
        user_id: userId,
        email: userEmail,
        email_marketing_consent: false,
        email_preferences: {},
        consent_timestamp: new Date().toISOString(),
        gdpr_applicable: isEUUser,
      };

      // Update Auth0 to mark terms as accepted
      await updateAuth0UserMetadata(consentData);
    } catch (err) {
      console.error('Error updating terms acceptance:', err);
      // Continue anyway - don't block user
    }

    // Redirect to the app
    window.location.href = returnUrl;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const logo = `${window.ASSETS_BASE_URL || window.location.origin}/images/BKFC.png`;

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center px-4">
      <div className="max-w-lg w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <img src={logo} alt="BKFC+" className="h-20 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-2">Welcome to BKFC+!</h1>
          <p className="text-gray-400 text-lg">Your account has been created successfully</p>
        </div>

        {/* Main Content */}
        <div className="bg-gray-800 rounded-lg p-8 shadow-xl">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">Stay Updated with BKFC+</h2>
            <p className="text-gray-300 text-sm">
              Get personalized recommendations and discover trending content
            </p>
          </div>

          {/* GDPR Notice for EU Users */}
          {isEUUser && (
            <div className="mb-6 p-4 bg-yellow-600/20 border border-yellow-600/50 rounded-lg">
              <div className="flex items-start">
                <div className="text-yellow-400 mr-3 mt-0.5">ℹ️</div>
                <div>
                  <p className="text-yellow-200 text-sm font-medium mb-1">EU Privacy Notice</p>
                  <p className="text-yellow-300 text-xs">
                    Email marketing is completely optional. You can change your preferences anytime
                    in your account settings or unsubscribe from any email.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-red-600/20 border border-red-600/50 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Main Email Consent */}
            <div className="space-y-4">
              <label className="flex items-start space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={emailConsent}
                  onChange={e => setEmailConsent(e.target.checked)}
                  className="mt-1 w-5 h-5 text-accent bg-gray-700 border-gray-600 rounded focus:ring-accent focus:ring-2"
                />
                <div className="flex-1">
                  <div className="text-white font-medium group-hover:text-accent transition-colors">
                    📧 Yes, send me email updates from BKFC+
                  </div>
                  <div className="text-gray-400 text-sm mt-1">
                    Get trending content alerts, new release notifications, and personalized
                    recommendations
                  </div>
                </div>
              </label>

              {/* Email Preferences (shown when consent is given) */}
              {emailConsent && (
                <div className="ml-8 space-y-3 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                  <p className="text-white text-sm font-medium mb-3">
                    What would you like to receive?
                  </p>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailPreferences.trending}
                      onChange={e =>
                        setEmailPreferences(prev => ({
                          ...prev,
                          trending: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-accent bg-gray-600 border-gray-500 rounded focus:ring-accent"
                    />
                    <span className="text-gray-300 text-sm">🔥 Trending content alerts</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailPreferences.newReleases}
                      onChange={e =>
                        setEmailPreferences(prev => ({
                          ...prev,
                          newReleases: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-accent bg-gray-600 border-gray-500 rounded focus:ring-accent"
                    />
                    <span className="text-gray-300 text-sm">🎬 New release notifications</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailPreferences.personalized}
                      onChange={e =>
                        setEmailPreferences(prev => ({
                          ...prev,
                          personalized: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-accent bg-gray-600 border-gray-500 rounded focus:ring-accent"
                    />
                    <span className="text-gray-300 text-sm">✨ Personalized recommendations</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailPreferences.newsletter}
                      onChange={e =>
                        setEmailPreferences(prev => ({
                          ...prev,
                          newsletter: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-accent bg-gray-600 border-gray-500 rounded focus:ring-accent"
                    />
                    <span className="text-gray-300 text-sm">📰 Weekly newsletter</span>
                  </label>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent hover:bg-accent/80 disabled:bg-accent/50 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving Preferences...
                  </>
                ) : emailConsent ? (
                  'Save Preferences & Continue'
                ) : (
                  'Continue Without Emails'
                )}
              </button>

              <button
                type="button"
                onClick={handleSkip}
                className="w-full bg-transparent border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Skip for Now
              </button>
            </div>

            {/* Privacy Notice */}
            <div className="text-center">
              <p className="text-gray-500 text-xs">
                You can change these preferences anytime in your account settings. We respect your
                privacy and will never share your email with third parties.
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-gray-500 text-sm">Powered by BKFC+'s AI-driven content discovery</p>
        </div>
      </div>
    </div>
  );
};

export default EmailConsentPage;
